import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { useAuth } from '../AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ui/ThemeToggle';
import { API_BASE_URL } from '../lib/api';
import './OAuthConsentPage.css';

/**
 * OAuth consent page — `/oauth/consent?request=<id>`.
 *
 * Issue 3 of .claude/docs/tasks/mcp-oauth/context.md (issue #257). Reached
 * when `GET /oauth/authorize` (the backend authorization server, issue #255)
 * redirects here for a third-party OAuth client (e.g. ChatGPT's Developer
 * Mode connector) requesting access to this user's portfolio data.
 *
 * Security-relevant behavior, all deliberate:
 * - The client name and redirect host come from `GET /api/oauth/requests/:id`
 *   and are rendered as plain React text nodes only — never
 *   `dangerouslySetInnerHTML` — so a malicious client_name registered via
 *   DCR can't inject markup here.
 * - The redirect HOST is shown prominently (not just the client's
 *   self-declared name) with an explicit warning when it resolves to
 *   localhost/127.0.0.1, since the client name is attacker-controlled but
 *   the host is what the browser will actually be sent to.
 * - No auto-approve: the request is only ever decided by an explicit click.
 * - The shared demo account can't approve (backend: 403 demo_not_allowed) —
 *   surfaced here proactively via `user.isDemoUser` so the button is simply
 *   never offered, rather than shown and then rejected.
 * - After a decision, the redirect is only followed when
 *   `new URL(redirectUrl).protocol` is `http:` or `https:` — defense in
 *   depth against the backend ever handing back something else.
 */

const SCOPE_LABELS = {
  'portfolio:read': 'View your portfolio, holdings, transactions and analytics',
  'portfolio:write': 'Add, edit and delete your holdings, transactions and other portfolio data',
};

function describeScope(scope) {
  return SCOPE_LABELS[scope] || scope;
}

function isSafeRedirect(url) {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function ConsentShell({ children }) {
  return (
    <div className="oauth-consent-container">
      <div className="theme-toggle-wrapper">
        <ThemeToggle />
      </div>
      <Card className="oauth-consent-card" padding="lg">
        {children}
      </Card>
    </div>
  );
}

function StatusMessage({ title, message }) {
  return (
    <ConsentShell>
      <h1 className="oauth-consent-title">{title}</h1>
      <p className="oauth-consent-message">{message}</p>
    </ConsentShell>
  );
}

function OAuthConsentPage() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request') || '';

  // status: 'loading' | 'loaded' | 'not-found' | 'error'
  const [status, setStatus] = React.useState('loading');
  const [request, setRequest] = React.useState(null);
  // 'approve' | 'deny' while a decision is in flight, so only the clicked button relabels.
  const [decisionPending, setDecisionPending] = React.useState(null);
  const [decisionError, setDecisionError] = React.useState(null);
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (authLoading || !isAuthenticated || !requestId) return undefined;

    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/oauth/requests/${encodeURIComponent(requestId)}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        setRequest(res.data);
        setStatus('loaded');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err.response?.status === 404 ? 'not-found' : 'error');
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, requestId]);

  const decide = async (decision) => {
    if (!request) return;
    setDecisionPending(decision);
    setDecisionError(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/oauth/authorize/decision`,
        { requestId: request.requestId, decision },
        { withCredentials: true }
      );
      const { redirectUrl } = res.data;
      if (!isSafeRedirect(redirectUrl)) {
        setDecisionError('Something went wrong. Please try again.');
        return;
      }
      setRedirecting(true);
      window.location.href = redirectUrl;
    } catch (err) {
      if (err.response?.status === 403 && err.response.data?.error === 'demo_not_allowed') {
        setDecisionError('The demo account cannot connect third-party apps.');
      } else if (err.response?.status === 404) {
        setStatus('not-found');
      } else {
        setDecisionError(
          err.response?.data?.message || 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setDecisionPending(null);
    }
  };

  // Still resolving the session — avoid flashing the logged-out redirect for
  // a user who is, in fact, authenticated.
  if (authLoading) {
    return (
      <ConsentShell>
        <p className="oauth-consent-message">Loading…</p>
      </ConsentShell>
    );
  }

  // Logged out: send to Login with the full path+query so it can resume this
  // flow after a successful sign-in (Login validates/consumes `returnTo`).
  if (!isAuthenticated) {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (!requestId) {
    return (
      <StatusMessage
        title="Invalid link"
        message="This authorization link is missing its request. Go back to the app you were connecting and try again."
      />
    );
  }

  if (status === 'loading') {
    return (
      <ConsentShell>
        <p className="oauth-consent-message">Loading authorization request…</p>
      </ConsentShell>
    );
  }

  if (status === 'not-found') {
    return (
      <StatusMessage
        title="This request has expired"
        message="This authorization request is no longer valid — it may have expired or already been used. Go back to the app you were connecting and try again."
      />
    );
  }

  if (status === 'error') {
    return (
      <StatusMessage
        title="Something went wrong"
        message="We couldn't load this authorization request. Please try again."
      />
    );
  }

  const isDemo = !!user?.isDemoUser;
  const scopes = request.scopes || [];

  return (
    <ConsentShell>
      <h1 className="oauth-consent-title">Connect app</h1>
      <p className="oauth-consent-message">
        {/* Plain text node only — client_name is attacker-controlled (DCR),
            so this is never rendered as HTML. */}
        <strong>{request.clientName}</strong> wants to access your Portfolio
        Tracker account.
      </p>

      <div className="oauth-consent-redirect-box">
        <div className="oauth-consent-redirect-label">
          After approving, you&apos;ll be redirected to
        </div>
        <div className="oauth-consent-redirect-host">{request.redirectHost}</div>
        <p className="oauth-consent-redirect-hint">
          The app name above is chosen by the app itself. Only continue if you
          recognize this address and started this connection yourself.
        </p>
      </div>

      {request.isLocalhostRedirect && (
        <div className="oauth-consent-banner oauth-consent-banner-warning">
          This app will redirect to a local address on this device
          ({request.redirectHost}). Only continue if you are setting up a
          developer tool or local application you trust.
        </div>
      )}

      <div className="oauth-consent-scopes">
        <div className="oauth-consent-scopes-label">This app will be able to:</div>
        <ul className="oauth-consent-scopes-list">
          {scopes.map((scope) => (
            <li key={scope}>{describeScope(scope)}</li>
          ))}
        </ul>
      </div>

      {isDemo && (
        <div className="oauth-consent-banner oauth-consent-banner-info">
          You&apos;re signed in to the shared demo account, which can&apos;t
          connect third-party apps. Sign in with your own account to continue.
        </div>
      )}

      {decisionError && (
        <div className="oauth-consent-banner oauth-consent-banner-error">{decisionError}</div>
      )}

      {redirecting && <p className="oauth-consent-message">Redirecting…</p>}

      <div className="oauth-consent-actions">
        {!isDemo && (
          <Button
            variant="primary"
            fullWidth
            disabled={decisionPending || redirecting}
            onClick={() => decide('approve')}
          >
            {decisionPending === 'approve' ? 'Connecting…' : 'Approve'}
          </Button>
        )}
        <Button
          variant="secondary"
          fullWidth
          disabled={decisionPending || redirecting}
          onClick={() => decide('deny')}
        >
          Deny
        </Button>
      </div>
    </ConsentShell>
  );
}

export default OAuthConsentPage;
